import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { brand, error, gray } from "../../../../theme/themePrimitives";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../utils/inputLimits";
import { GetUserAttributesParamsByID } from "../../../../services/api/types/portalConfiguration/userAttribute";
import { getUserAttribute } from "../../../../services/api/apiClient";
import { useToast } from "../../../../context/ToastContext";

interface AddNewAttributeModalProps {
  order: number;
  attribute: string;
  attributeError: string;
  handleAttributeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  attributeKey: string;
  attributeKeyError: string;
  handleAttributeKeyChange: (e: ChangeEvent<HTMLInputElement>) => void;
  dataType: string;
  dataTypeError: string;
  handleDataTypeChange: (e: SelectChangeEvent<string>) => void;
  mandatory: boolean;
  mandatoryError: string;
  handleMandatoryChange: (e: any) => void;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setAttributeKeyError: React.Dispatch<React.SetStateAction<string>>;
  isEditing: boolean;
  addNewAttribute: () => void;
  isAvailable: boolean;
  handleIsAvailableChange: (e: any) => void;
  isLineManager: boolean;
  handleIsLineManager: (e: any) => void;
  isLineManagerError: string;
  isEditable: boolean;
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
  setAttributeKey: React.Dispatch<React.SetStateAction<string>>;
  buttonLoading: boolean;
  currentAttributeData: GetUserAttributesParamsByID;
}

export const AddNewAttributeModal: React.FC<AddNewAttributeModalProps> = ({
  openModal,
  setOpenModal,
  addNewAttribute,
  attribute,
  attributeError,
  handleAttributeChange,
  attributeKey,
  attributeKeyError,
  dataType,
  dataTypeError,
  handleAttributeKeyChange,
  handleDataTypeChange,
  handleMandatoryChange,
  mandatory,
  mandatoryError,
  order,
  isAvailable,
  handleIsAvailableChange,
  isEditing,
  setAttributeKey,
  setOpenConfirmModal,
  setAttributeKeyError,
  buttonLoading,
  isLineManager,
  handleIsLineManager,
  isLineManagerError,
  isEditable,
  setIsEditable,
  currentAttributeData,
}) => {
  const [portalHasUsers, setPortalHasUsers] = useState(false);
  const { showToast } = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAttributeData = async () => {
    if (
      !currentAttributeData.portalId ||
      !currentAttributeData.userAttributeId
    ) {
      return;
    }
    try {
      const response = await getUserAttribute(
        currentAttributeData.portalId,
        currentAttributeData.userAttributeId,
        currentAttributeData.lang,
      );
      setPortalHasUsers(Boolean(response?.portalHasUsers));
      setIsEditable(response?.isEditable ?? false);
    } catch (error: any) {
      console.log("Error:", error);
      if (error.response.data === "No user attributes found!") return;
      showToast(error.response.data, "error");
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    if (!openModal) {
      setPortalHasUsers(false);
    }
    if (openModal && isEditing) {
      fetchAttributeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, isEditing]);

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "24px",
          width: "480px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "16px",
            borderBottom: `1px solid ${gray[300]}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "28px",
              color: "#101828",
            }}
          >
            {isEditing ? "Edit Attribute" : "Add New Attribute"}
          </Typography>
          <IconButton
            sx={{
              border: "none",
            }}
            onClick={() => setOpenModal(false)}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>
        <Stack spacing={3} sx={{ marginTop: "1rem" }}>
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#344054",
              }}
            >
              Attribute
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="attribute"
              placeholder="Enter attribute name"
              type="text"
              value={attribute}
              onBlur={() => {
                if (isEditing) return;
                setAttributeKey(attribute.toLowerCase().replace(/\s+/g, "_"));
                setAttributeKeyError("");
              }}
              onChange={handleAttributeChange}
              variant="outlined"
              size="small"
              error={Boolean(attributeError)}
              helperText={attributeError}
              slotProps={{
                input: {
                  endAdornment: attributeError && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#344054",
              }}
            >
              Attribute Key
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="attributeKey"
              placeholder="attribute_key"
              disabled={isEditing}
              type="text"
              value={attributeKey}
              onChange={handleAttributeKeyChange}
              variant="outlined"
              size="small"
              error={Boolean(attributeKeyError)}
              helperText={attributeKeyError}
              slotProps={{
                input: {
                  endAdornment: attributeKeyError && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
            />
          </Stack>
          <Stack spacing={1.5}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#344054",
                  }}
                >
                  Mandatory
                </Typography>
                <Switch
                  onClick={(e) => {
                    if (isEditing) {
                      e.preventDefault();
                      setOpenConfirmModal(true);
                    }
                  }}
                  checked={mandatory}
                  onChange={(e) => {
                    if (!isEditing) {
                      handleMandatoryChange(e);
                    }
                  }}
                />
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#344054",
                  }}
                >
                  Available
                </Typography>
                <Switch
                  checked={isAvailable}
                  onChange={handleIsAvailableChange}
                />
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Box
                onClick={() => {
                  if (!isAvailable) return;
                  setIsEditable(false);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: isAvailable ? "pointer" : "not-allowed",
                  opacity: isAvailable ? 1 : 0.5,
                }}
              >
                <Radio
                  checked={isAvailable && !isEditable}
                  disabled={!isAvailable}
                  onChange={() => setIsEditable(false)}
                  value="view-only"
                  sx={{
                    p: 0,
                    color: gray[300],
                    "&.Mui-checked": {
                      color: "#12B76A",
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: gray[700],
                  }}
                >
                  View Only
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  if (!isAvailable) return;
                  setIsEditable(true);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: isAvailable ? "pointer" : "not-allowed",
                  opacity: isAvailable ? 1 : 0.5,
                }}
              >
                <Radio
                  checked={isAvailable && isEditable}
                  disabled={!isAvailable}
                  onChange={() => setIsEditable(true)}
                  value="editable"
                  sx={{
                    p: 0,
                    color: gray[300],
                    "&.Mui-checked": {
                      color: "#12B76A",
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: gray[700],
                  }}
                >
                  Editable
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#344054",
                  }}
                >
                  Escalation Email(s)
                </Typography>
                <Switch
                  checked={isLineManager}
                  onChange={handleIsLineManager}
                />
              </Box>
              {isLineManagerError && (
                <Typography
                  sx={{ margin: "4px 4px 0px !important" }}
                  fontSize={"12px"}
                  color={error[600]}
                >
                  {isLineManagerError}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#344054",
              }}
            >
              Data Type
            </Typography>
            <Select
              id="add-new-attribute-data-type-select"
              IconComponent={ExpandMoreIcon}
              value={dataType}
              displayEmpty
              disabled={isEditing || portalHasUsers}
              error={Boolean(dataTypeError)}
              onChange={handleDataTypeChange}
              sx={{
                borderRadius: "8px",
                minWidth: "150px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
              renderValue={(selected) =>
                selected ? (
                  selected
                ) : (
                  <Typography sx={{ color: "#98A2B3" }}>
                    Select data type
                  </Typography>
                )
              }
            >
              <MenuItem value="" disabled sx={{ my: 0.5 }}>
                Select data type
              </MenuItem>
              <MenuItem value={"string"} sx={{ my: 0.5 }}>
                string
              </MenuItem>
              <MenuItem value={"number"} sx={{ my: 0.5 }}>
                number
              </MenuItem>
              <MenuItem value={"date"} sx={{ my: 0.5 }}>
                date
              </MenuItem>
              <MenuItem value={"boolean"} sx={{ my: 0.5 }}>
                boolean
              </MenuItem>
            </Select>
            {dataTypeError && (
              <Typography
                sx={{ margin: "4px 4px 0px !important" }}
                fontSize={"12px"}
                color={error[600]}
              >
                {dataTypeError}
              </Typography>
            )}
          </Stack>
          {/* <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              color={textColorsSettings?.gpcPrimaryTextColor}
            >
              {addNewAttributeTranslations?.orderNumber}
            </Typography>
            <TextField
              fullWidth
              id="order"
              type="text"
              disabled
              value={order ?? 1}
              variant="outlined"
              size="small"
              slotProps={{
                input: {},
              }}
            />
          </Stack> */}
          {buttonLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Button
              onClick={addNewAttribute}
              fullWidth
              sx={{
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                  fontWeight: 700,
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Save
              </Typography>
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};
